import { clsx } from 'clsx';
import {
  forwardRef,
  memo,
  useContext,
  useMemo,
  useRef,
  type ComponentPropsWithoutRef,
  type MutableRefObject,
  type RefObject,
} from 'react';
import { useIntl } from 'react-intl';

import * as styles from './DetailsLayout.sty.js';
import { DetailsLayoutContext } from './DetailsLayoutContext.js';
import { Overlay, OVERLAY_HANDLE_WIDTH } from './Overlay.js';
import {
  type ExtractedContentSlot,
  type ExtractedDetailsSlot,
} from './slots.js';
import { useLayoutState } from './useLayoutState.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { useSafeLayoutEffect } from '../../../core/hooks/useSafeLayoutEffect.js';
import { Drawer } from '../../../overlays/drawer/Drawer.js';
import { SplitLayout } from '../../split-layout/SplitLayout.js';
import { convertToPx } from '../../split-layout/width-utils.js';
import {
  PageLayoutPortalContext,
  type PageLayoutPortalContextValue,
} from '../shared/PageLayoutPortalContext.js';
import { useTeleport } from '../shared/useTeleport.js';

type TeleportPair = ReturnType<typeof useTeleport>;

function DetailsLayoutInner({
  details,
  content,
  containerRef,
  persistedPanelWidthRef,
  contentTeleport,
  panelTeleport,
}: {
  details: ExtractedDetailsSlot;
  content: ExtractedContentSlot;
  containerRef: RefObject<HTMLDivElement>;
  persistedPanelWidthRef: MutableRefObject<number | undefined>;
  contentTeleport: TeleportPair;
  panelTeleport: TeleportPair;
}) {
  const intl = useIntl();
  const layout = useLayoutState({ details, containerRef });

  const detailsAriaLabel = intl.formatMessage({
    defaultMessage: 'Details',
    id: 'o3A99RFWS+N7bJe8',
    description: 'Accessible label for the details panel.',
  });

  const contextValue = useMemo(
    () => ({
      collapsed: layout.collapsed,
      setCollapsed: layout.setCollapsed,
      preferredLayout: layout.preferredLayoutMode,
      setPreferredLayout: layout.setPreferredLayoutMode,
      mode: layout.mode,
    }),
    [
      layout.collapsed,
      layout.setCollapsed,
      layout.preferredLayoutMode,
      layout.setPreferredLayoutMode,
      layout.mode,
    ],
  );

  // Bridge: DetailsLayoutContext.Provider below only covers the split/overlay
  // DOM rendered by this component. PageLayout.Details children live in a
  // different React subtree (they're portaled), so the provider doesn't reach
  // them. Publish the value through the registry so PageLayoutDetails can
  // re-wrap its portaled children with a provider of the same value.
  const pageLayoutPortalContext =
    useContext<PageLayoutPortalContextValue | null>(PageLayoutPortalContext);
  useSafeLayoutEffect(() => {
    if (!pageLayoutPortalContext) {
      return;
    }
    pageLayoutPortalContext.registry.register(
      'detailsLayoutContext',
      contextValue,
    );
  }, [pageLayoutPortalContext, contextValue]);

  const contentNode = (
    <div
      ref={content.ref}
      {...content.domProps}
      className={clsx(styles.content, content.domProps.className)}
    >
      <contentTeleport.Target />
    </div>
  );

  const layoutNode = (() => {
    switch (layout.mode) {
      case 'collapsed': {
        return contentNode;
      }
      case 'split': {
        return (
          <SplitLayout>
            <SplitLayout.Content
              {...content.domProps}
              ref={content.ref}
              minWidth={content.config.minWidth}
              className={clsx(styles.content, content.domProps.className)}
            >
              <contentTeleport.Target />
            </SplitLayout.Content>
            <SplitLayout.Panel
              {...details.domProps}
              ref={details.ref}
              minWidth={details.config.minWidth}
              maxWidth={details.config.maxWidth}
              defaultWidth={
                persistedPanelWidthRef.current ?? details.config.defaultWidth
              }
              onResize={(value) => {
                persistedPanelWidthRef.current = value;
                details.config.onResize?.(value);
              }}
              resizable={details.config.resizable}
              position="end"
              className={clsx(styles.panel, details.domProps.className)}
              aria-label={detailsAriaLabel}
            >
              <panelTeleport.Target />
            </SplitLayout.Panel>
          </SplitLayout>
        );
      }
      case 'overlay': {
        // Defer Overlay until container is measured
        // Render a hidden panel Target so the teleport host stays attached
        // across the measure frame.
        if (layout.containerWidth === 0) {
          return (
            <>
              {contentNode}
              <div hidden aria-hidden="true">
                <panelTeleport.Target />
              </div>
            </>
          );
        }
        return (
          <>
            {contentNode}
            <Overlay
              {...details.domProps}
              ref={details.ref}
              minWidth={convertToPx(
                details.config.minWidth,
                layout.containerWidth,
              )}
              maxWidth={
                details.config.resizable
                  ? layout.containerWidth - OVERLAY_HANDLE_WIDTH
                  : layout.containerWidth
              }
              defaultWidth={
                persistedPanelWidthRef.current ??
                convertToPx(details.config.defaultWidth, layout.containerWidth)
              }
              resizable={details.config.resizable}
              onResize={(value) => {
                persistedPanelWidthRef.current = value;
                // TODO: should we communicate which layout mode was updated?
                details.config.onResize?.(value);
              }}
            >
              <panelTeleport.Target />
            </Overlay>
          </>
        );
      }
      case 'drawer': {
        return (
          <>
            {contentNode}
            <Drawer
              {...details.domProps}
              ref={details.ref}
              className={clsx(styles.drawer, details.domProps.className)}
              aria-label={detailsAriaLabel}
              isDismissed={layout.collapsed}
              onDismiss={(state) => layout.setCollapsed(state)}
              placement="right"
              width="wide"
            >
              <div className={styles.drawerContent}>
                <panelTeleport.Target />
              </div>
            </Drawer>
          </>
        );
      }
    }
  })();

  return (
    <DetailsLayoutContext.Provider value={contextValue}>
      <panelTeleport.Source>{details.children}</panelTeleport.Source>
      {layoutNode}
    </DetailsLayoutContext.Provider>
  );
}

/** @internal */
export type LayoutRootProps = {
  details: ExtractedDetailsSlot | null;
  content: ExtractedContentSlot;
} & Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'content'>;

/** @internal */
export const DetailsLayout = memo(
  forwardRef<HTMLDivElement, LayoutRootProps>((props, ref) => {
    const { className: rootClassName, details, content, ...rootProps } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const rootRef = useMergeRefs([ref, containerRef]);
    const persistedPanelWidth = useRef<number>();

    const contentTeleport = useTeleport();
    const panelTeleport = useTeleport();

    return (
      <>
        <contentTeleport.Source>{content.children}</contentTeleport.Source>
        <div
          {...rootProps}
          ref={rootRef}
          className={clsx(styles.layoutRoot, rootClassName)}
        >
          {details ? (
            <DetailsLayoutInner
              details={details}
              content={content}
              containerRef={containerRef}
              persistedPanelWidthRef={persistedPanelWidth}
              contentTeleport={contentTeleport}
              panelTeleport={panelTeleport}
            />
          ) : (
            <div
              ref={content.ref}
              {...content.domProps}
              className={clsx(styles.content, content.domProps.className)}
            >
              <contentTeleport.Target />
            </div>
          )}
        </div>
      </>
    );
  }),
);
DetailsLayout.displayName = 'DetailsLayout (forwardRef)';
